import React from "react";
import { PhoneMissed, Voicemail, CalendarX, Clock, Waypoints, Funnel } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const frictionPoints = [{
  title: "Missed Opportunities",
  description: "62% of calls to service businesses go unanswered. Every missed call is a potential customer moving on to your competitor.",
  icon: PhoneMissed
}, {
  title: "The Voicemail Black Hole",
  description: "85% of customers hang up rather than leaving a voicemail. Silence isn't just quiet; it's an expensive leak in your funnel.",
  icon: Voicemail
}, {
  title: "Scheduling Friction",
  description: "Hours wasted in email tag and phone tag just to find a meeting time. Manual scheduling kills deal momentum.",
  icon: CalendarX
}, {
  title: "After-Hours Silence",
  description: "Your business closes, but customer needs don't. Unanswered after-hours calls are leads gifted to 24/7 competitors.",
  icon: Clock
}, {
  title: "Inefficient Routing",
  description: "Manual call transfers and wrong departments frustrate callers and waste valuable staff time with every hop.",
  icon: Waypoints
}, {
  title: "Lead Leakage",
  description: "Without instant qualification, high-value leads get buried under spam and low-priority inquiries, lost forever.",
  icon: Funnel
}];
export default function TheCostOfSilence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="the-cost-of-silence" ref={ref} className="bg-[#121214] py-24 md:py-32 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className={cn("max-w-3xl mx-auto text-center mb-20 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-white mb-6">
            The Cost of Silence
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            In a world that demands instant answers, every second of silence costs you revenue. 
            Here is where traditional phone systems fail.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {frictionPoints.map((item, i) => <div key={i} data-index={i} className={cn("transition-all duration-700 delay-[100ms]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")} style={{
          transitionDelay: `${i * 100}ms`
        }}>
              <Card className="group h-full bg-white/5 backdrop-blur-md border border-white/10 rounded-[10px] shadow-2xl shadow-black/20 hover:border-primary/50 hover:-translate-y-1 transition-all duration-500 relative overflow-hidden py-0">
                <div className="absolute top-0 left-0 w-full h-1 bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                
                <CardHeader className="pb-2 pt-8">
                  <div className="mb-6 inline-flex items-center justify-center size-16 rounded-full bg-primary/10 ring-1 ring-primary/20 group-hover:bg-primary/20 transition-all duration-300">
                    <item.icon className="size-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <CardTitle className="text-2xl font-bold text-white group-hover:text-primary transition-colors duration-300">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-8">
                  <CardDescription className="text-base leading-relaxed text-white/60">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}